<template>
  <b-row>
    <!-- Revenue Trends Mini Chart -->
    <b-col
      cols="12"
      class="mb-4"
    >
      <b-card no-body>
        <b-card-header>
          <h4 class="mb-0">
            Umsatz-Trend (Letzte 6 Monate)
          </h4>
          <small class="text-muted">Monatliche Entwicklung</small>
        </b-card-header>
        
        <b-card-body>
          <apexchart
            type="area"
            height="200"
            :options="trendChartOptions"
            :series="trendChartSeries"
          />
          
          <!-- Trend Statistics -->
          <b-row class="mt-3">
            <b-col
              cols="6"
              md="3"
              class="text-center"
            >
              <h6 class="text-primary mb-0">
                {{ formatCurrency(avgMonthlyRevenue) }}
              </h6>
              <small class="text-muted">Durchschnitt/Monat</small>
            </b-col>
            <b-col
              cols="6"
              md="3"
              class="text-center"
            >
              <h6
                :class="growthTrendClass"
                class="mb-0"
              >
                {{ growthPercentage }}%
              </h6>
              <small class="text-muted">Wachstum</small>
            </b-col>
            <b-col
              cols="6"
              md="3"
              class="text-center"
            >
              <h6 class="text-info mb-0">
                {{ formatCurrency(bestMonth.revenue) }}
              </h6>
              <small class="text-muted">Bester Monat</small>
            </b-col>
            <b-col
              cols="6"
              md="3"
              class="text-center"
            >
              <h6 class="text-success mb-0">
                {{ activeRentalsCount }}
              </h6>
              <small class="text-muted">Aktive Mieten</small>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>
    </b-col>
  </b-row>
</template>

<script>
import { BCard, BCardHeader, BCardBody, BRow, BCol, BBadge, BDropdown, BDropdownItem } from 'bootstrap-vue'
import ApexCharts from 'vue-apexcharts'
import { $themeColors } from '@themeConfig'
import businessMetrics from '@/helper/businessMetrics'

export default {
  name: 'RevenueBreakdownCharts',
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BBadge,
    BDropdown,
    BDropdownItem,
    apexchart: ApexCharts,
  },
  setup() {
    const { getOrderItemsMonthlyRevenue, getRevenueTillNow, getRentedStorages } = businessMetrics()
    return {
      getOrderItemsMonthlyRevenue,
      getRevenueTillNow,
      getRentedStorages,
    }
  },
  data() {
    return {
      selectedPeriod: 'current',
      timePeriods: [
        { value: 'current', label: 'Aktueller Monat' },
        { value: 'last3months', label: 'Letzte 3 Monate' },
        { value: 'last6months', label: 'Letzte 6 Monate' },
        { value: 'last12months', label: 'Letztes Jahr' },
      ],
      storageSizes: [],
      monthlyTrends: [],
      avgMonthlyRevenue: 0,
      growthPercentage: 0,
      bestMonth: { revenue: 0, month: '' },
      activeRentalsCount: 0,
      storageDonutSeries: [],
      trendChartSeries: [],
      storageDonutOptions: {
        chart: {
          type: 'donut',
          fontFamily: 'inherit',
        },
        colors: [
          '#FF9F43',
          '#28C76F',
          '#EA5455',
          '#FF6B6B',
          '#4ECDC4',
          '#45B7D1',
        ],
        legend: {
          show: false,
        },
        plotOptions: {
          pie: {
            donut: {
              size: '65%',
              labels: {
                show: true,
                name: {
                  fontSize: '16px',
                  fontWeight: 600,
                },
                value: {
                  fontSize: '14px',
                  formatter: val => this.formatCurrency(val),
                },
                total: {
                  show: true,
                  label: 'Gesamt',
                  fontSize: '14px',
                  formatter: () => this.formatCurrency(this.storageSizes.reduce((sum, size) => sum + size.revenue, 0)),
                },
              },
            },
          },
        },
        dataLabels: {
          enabled: false,
        },
        tooltip: {
          y: {
            formatter: val => this.formatCurrency(val),
          },
        },
      },
      trendChartOptions: {
        chart: {
          type: 'area',
          height: 200,
          toolbar: { show: false },
          fontFamily: 'inherit',
          sparkline: { enabled: false },
        },
        colors: [$themeColors.primary],
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.7,
            opacityTo: 0.1,
            stops: [0, 90, 100],
          },
        },
        stroke: {
          curve: 'smooth',
          width: 2,
        },
        grid: {
          borderColor: '#e4e6e8',
          strokeDashArray: 3,
        },
        xaxis: {
          categories: [],
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: {
            style: {
              colors: '#6c757d',
              fontSize: '12px',
            },
          },
        },
        yaxis: {
          labels: {
            style: {
              colors: '#6c757d',
              fontSize: '12px',
            },
            formatter: val => this.formatCurrency(val, true),
          },
        },
        tooltip: {
          y: {
            formatter: val => this.formatCurrency(val),
          },
        },
        dataLabels: {
          enabled: false,
        },
      },
    }
  },
  computed: {
    growthTrendClass() {
      if (this.growthPercentage > 0) return 'text-success'
      if (this.growthPercentage < 0) return 'text-danger'
      return 'text-muted'
    },
  },
  mounted() {
    this.loadRevenueData()
  },
  methods: {
    loadRevenueData() {
      this.loadStorageSizeRevenue()
      this.loadRevenueTrends()
    },
    loadStorageSizeRevenue() {
      try {
        if (!this.$store || !this.$store.getters) {
          this.storageSizes = []
          this.storageDonutSeries = []
          return
        }
        
        const storages = this.$store.getters.storageEntries || []
        const sizeRevenueData = {}
        
        console.log('Total storages to process:', storages.length)
        
        storages.forEach((storage, index) => {
          if (!storage) return
          
          // Check multiple possible field names for storage size
          const storageSize = storage.sizeM2 || storage.size_m2 || storage.size || storage.sizeInM2
          
          // Log first few storages for debugging
          if (index < 3) {
            console.log('Storage:', storage.storageID, 'Size:', storageSize, 'Available fields:', Object.keys(storage))
          }
          
          const sizeCategory = this.categorizeSizeM2(storageSize)
          
          if (!sizeRevenueData[sizeCategory]) {
            sizeRevenueData[sizeCategory] = { revenue: 0, count: 0 }
          }
          
          sizeRevenueData[sizeCategory].count += 1
          
          if (storage.order_items && storage.order_items.length > 0) {
            const activeItems = storage.order_items.filter(item => item && !item.moveOutDate)
            activeItems.forEach(item => {
              sizeRevenueData[sizeCategory].revenue += item.price || 0
            })
          }
        })
        
        console.log('Size revenue data:', sizeRevenueData)
        
        this.storageSizes = Object.entries(sizeRevenueData).map(([name, data]) => ({
          name,
          revenue: data.revenue,
          count: data.count,
        }))
        
        this.storageDonutSeries = this.storageSizes.map(size => size.revenue)
      } catch (error) {
        console.error('Error loading storage size revenue:', error)
        this.storageSizes = []
        this.storageDonutSeries = []
      }
    },
    loadRevenueTrends() {
      try {
        const monthlyData = this.getOrderItemsMonthlyRevenue() || {}
        const months = Object.keys(monthlyData)
        const revenues = Object.values(monthlyData).map(data => data?.sum || 0)
      
      this.monthlyTrends = months.map((month, index) => ({
        month,
        revenue: revenues[index] || 0,
      }))
      
      this.trendChartOptions = {
        ...this.trendChartOptions,
        xaxis: {
          ...this.trendChartOptions.xaxis,
          categories: months,
        },
      }
      
      this.trendChartSeries = [
        {
          name: 'Umsatz',
          data: revenues,
        },
      ]
      
        // Calculate statistics
        const validRevenues = revenues.filter(r => r > 0)
        this.avgMonthlyRevenue = validRevenues.length > 0 
          ? validRevenues.reduce((sum, rev) => sum + rev, 0) / validRevenues.length 
          : 0
        
        if (validRevenues.length >= 2) {
          const lastRevenue = validRevenues[validRevenues.length - 1]
          const previousRevenue = validRevenues[validRevenues.length - 2]
          this.growthPercentage = previousRevenue > 0 
            ? Math.round(((lastRevenue - previousRevenue) / previousRevenue) * 100)
            : 0
        }
        
        const maxRevenue = Math.max(...revenues)
        const maxIndex = revenues.indexOf(maxRevenue)
        this.bestMonth = {
          revenue: maxRevenue,
          month: months[maxIndex] || '',
        }
      } catch (error) {
        console.error('Error loading revenue trends:', error)
        this.monthlyTrends = []
        this.trendChartSeries = []
      }
    },
    updatePeriod(period) {
      this.selectedPeriod = period
      this.loadStorageSizeRevenue()
    },
    categorizeSizeM2(sizeM2) {
      // Convert to number if it's a string
      const size = parseFloat(sizeM2)
      
      if (!sizeM2 || Number.isNaN(size) || size <= 0) {
        console.warn('Invalid storage size:', sizeM2)
        return 'Unbekannt'
      }
      
      if (size <= 5) return 'Klein (≤5m²)'
      if (size <= 10) return 'Mittel (6-10m²)'
      if (size <= 20) return 'Groß (11-20m²)'
      if (size <= 50) return 'Extra Groß (21-50m²)'
      return 'XXL (>50m²)'
    },
    formatCurrency(amount, abbreviated = false) {
      if (!amount) return '0 €'
      
      if (abbreviated && amount >= 1000) {
        return `${(amount / 1000).toFixed(1)}k €`
      }
      
      return `${amount.toLocaleString('de-DE', { maximumFractionDigits: 0 })} €`
    },
  },
}
</script>

<style scoped>
.revenue-chart-container,
.storage-chart-container {
  position: relative;
}

.metric-row {
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.metric-row:last-child {
  border-bottom: none;
}

.metric-color-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
}

.metric-label {
  font-size: 0.875rem;
  color: #6c757d;
}

.metric-values strong {
  font-size: 0.875rem;
  color: #495057;
}

.metric-values small {
  font-size: 0.75rem;
}

.revenue-metrics,
.storage-metrics {
  max-height: 200px;
  overflow-y: auto;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .metric-label,
  .metric-values strong {
    font-size: 0.8rem;
  }
  
  .metric-values small {
    font-size: 0.7rem;
  }
}
</style>
